/**
 * The accounts screens' route registration: the monitored-accounts list
 * (frame 105:6) and the open account (frame 75:2 / 299:10749), discovered by
 * `../routes.ts`'s glob. Three full-width routes -- `ACCOUNTS_PATH` the list,
 * `ACCOUNT_PATH` the account, and `ACCOUNT_SELECT_PATH` the Conta entry's
 * picker (`EntityPage`) -- not a shared two-column grid. The shell
 * topbar's trail names each, so none draws a breadcrumb of its own.
 *
 * `AddAccount` owns no route of its own (its own module doc): it is opened
 * from the list's "Adicionar conta" button, controlled from state held here.
 *
 * The tracking editor (`../../control/paths.ts`'s `TRACKING_EDITOR_PATH`) is
 * a separate route this screen's own "Editar configuração" button navigates
 * to -- a different capability (choosing the account's collection profile)
 * from this one (a read overview with a rename control), so it keeps its own
 * URL.
 */

import { createElement, useState, type ReactNode } from 'react';
import { AtSign, Gauge, Users } from 'lucide-react';
import { useNavigate, useParams } from '@tanstack/react-router';

import { EntityPage } from '../../../chrome/entity/EntitySwitcher';
import type { RouteBundle } from '../../routes/contract';
import { ACCOUNTS_PATH, TRACKING_EDITOR_PATH } from '../../control/paths';
import { useCampaignOrganisationId, useCan } from '../../session/orgContext';
import { useTrackedAccounts } from './api/queries';
import { Account } from './Account';
import { Accounts } from './Accounts';
import { AddAccount } from './AddAccount';
import { am } from './messages';
import { AccountSectionsSkeleton } from './account/SectionsSkeleton';
import { accountOption } from './NoSelection';
import { ACCOUNT_PATH, ACCOUNT_SELECT_PATH } from './paths';

const CONTROL_PLANE_READ = 'control_plane.read';
const CONTROL_PLANE_WRITE = 'control_plane.write';

function useRouteParams(): { campaignId: string; accountId: string | undefined } {
  const { campaignId, accountId } = useParams({ strict: false }) as {
    campaignId?: string;
    accountId?: string;
  };
  return { campaignId: campaignId ?? '', accountId };
}

function AccountsListRoute(): ReactNode {
  const { campaignId } = useRouteParams();
  const tenantId = useCampaignOrganisationId();
  const navigate = useNavigate();
  const isAnalyst = !useCan(CONTROL_PLANE_WRITE);
  const [addAccountOpen, setAddAccountOpen] = useState(false);

  if (tenantId === null) return null;

  return createElement(
    'div',
    { className: 'flex flex-col gap-6' },
    createElement(AddAccount, {
      campaignId,
      tenantId,
      canWrite: !isAnalyst,
      open: addAccountOpen,
      onOpenChange: setAddAccountOpen,
    }),
    createElement(Accounts, {
      campaignId,
      isAnalyst,
      onSelectAccount: (id: string) =>
        void navigate({ to: ACCOUNT_PATH, params: { campaignId, accountId: id } }),
      onEditAccount: (id: string) =>
        void navigate({ to: TRACKING_EDITOR_PATH, params: { campaignId, accountId: id } }),
      onAddAccount: () => setAddAccountOpen(true),
    }),
  );
}

/** `ACCOUNT_SELECT_PATH` and `ACCOUNT_PATH` alike: an id the settled list does not hold draws
 *  the picker, the same as no id at all. */
function AccountRoute(): ReactNode {
  const { campaignId, accountId } = useRouteParams();
  const tenantId = useCampaignOrganisationId();
  const navigate = useNavigate();
  const isAnalyst = !useCan(CONTROL_PLANE_WRITE);
  const accountsQuery = useTrackedAccounts(campaignId);

  if (tenantId === null) return null;

  // An errored list still hands the id to `Account`, whose own error card reads the same query.
  const unknown =
    accountsQuery.isSuccess && !accountsQuery.data.some((row) => row.id === accountId);
  const selectedId = accountId === undefined || unknown ? null : accountId;

  return createElement(
    EntityPage,
    {
      scope: `account:${campaignId}`,
      options: accountsQuery.isSuccess
        ? accountsQuery.data.map(accountOption)
        : accountsQuery.isError
          ? 'failed'
          : 'loading',
      selectedId,
      labels: {
        select: am('noSelection.select'),
        title: am('noSelection.title'),
        search: am('noSelection.searchPlaceholder'),
        none: am('noSelection.none'),
      },
      onSelect: (id: string) =>
        void navigate({ to: ACCOUNT_PATH, params: { campaignId, accountId: id } }),
      placeholder: createElement(AccountSectionsSkeleton),
      embedded: true,
    },
    selectedId === null
      ? null
      : createElement(Account, {
          campaignId,
          accountId: selectedId,
          tenantId,
          isAnalyst,
          onBack: () => void navigate({ to: ACCOUNTS_PATH, params: { campaignId } }),
          onEdit: () =>
            void navigate({
              to: TRACKING_EDITOR_PATH,
              params: { campaignId, accountId: selectedId },
            }),
        }),
  );
}

export const bundle: RouteBundle = {
  routes: [
    {
      path: ACCOUNTS_PATH,
      element: createElement(AccountsListRoute),
      requires: CONTROL_PLANE_READ,
      kind: 'data',
    },
    {
      path: ACCOUNT_SELECT_PATH,
      element: createElement(AccountRoute),
      requires: CONTROL_PLANE_READ,
      kind: 'data',
    },
    {
      path: ACCOUNT_PATH,
      element: createElement(AccountRoute),
      requires: CONTROL_PLANE_READ,
      kind: 'data',
    },
  ],
  nav: [],
  campaignNav: [
    {
      path: ACCOUNTS_PATH,
      label: () => am('title'),
      requires: CONTROL_PLANE_READ,
      icon: AtSign,
      order: 40,
      kind: 'data',
    },
    {
      path: ACCOUNTS_PATH,
      label: () => am('nav.overview'),
      requires: CONTROL_PLANE_READ,
      parent: ACCOUNTS_PATH,
      icon: Gauge,
      order: 41,
      kind: 'data',
    },
    {
      path: ACCOUNT_SELECT_PATH,
      activeOn: ACCOUNT_PATH,
      label: () => am('nav.account'),
      requires: CONTROL_PLANE_READ,
      parent: ACCOUNTS_PATH,
      icon: Users,
      order: 55,
      kind: 'data',
    },
  ],
};
